import { ArrowForward } from "@mui/icons-material";
import { CircularProgress, IconButton } from "@mui/material";
import { FC, KeyboardEventHandler } from "react";
import { useTranslation } from "react-i18next";

import { AiAdvisorChatRenderer, ChatTextFieldStyled } from "@ryde/layout";
import { KeyboardKeys } from "@ryde/shared";

import {
  ASK_AI_INPUT_ID,
  ASK_AI_INPUT_ROWS,
  ASK_AI_MIN_CHARS,
  RIDE_INVESTIGATION_E2E,
} from "../investigation.constants";
import { InvestigationAskAiSuggestions } from "./investigation.ask-ai-suggestions";
import { AskAiSectionStyled } from "./investigation.ask-ai.styled";
import { useInvestigationAskAi } from "./use-investigation-ask-ai";

/** Ask-AI tab: a chat about this one ride, streamed from the existing advisor bot. */
export const InvestigationAskAi: FC<{ rideId?: number }> = ({ rideId }) => {
  const { t } = useTranslation();
  const {
    chatHistory,
    userMessage,
    textFieldRef,
    searchApply,
    sendElementValue,
    handleMessageChange,
    isLoading,
    isStreaming,
  } = useInvestigationAskAi(rideId);

  const isSendDisabled =
    userMessage.trim().length < ASK_AI_MIN_CHARS || isLoading;
  const sendLabel = t("rideMap.investigation.askAi.send");

  // Shift+Enter keeps its usual meaning — a newline — so a long question can still be written out.
  const onKeyDown: KeyboardEventHandler<HTMLDivElement> = event => {
    if (
      event.key === KeyboardKeys.ENTER &&
      !event.shiftKey &&
      !isSendDisabled
    ) {
      event.preventDefault();
      searchApply();
    }
  };

  return (
    <AskAiSectionStyled>
      <p className="ask-ai-intro">{t("rideMap.investigation.askAi.intro")}</p>

      {/* `aria-busy` while streaming holds the announcement back to the finished answer, instead of
          re-reading the reply on every chunk that lands. */}
      <div
        className="ask-ai-transcript"
        aria-live="polite"
        aria-busy={isStreaming}
      >
        {!chatHistory.length && (
          <InvestigationAskAiSuggestions onPick={sendElementValue} />
        )}
        <AiAdvisorChatRenderer
          chatHistory={chatHistory}
          isLoading={isLoading}
          isStreaming={isStreaming}
          onSendElementValue={sendElementValue}
        />
      </div>

      <div className="ask-ai-composer">
        <label className="ask-ai-label" htmlFor={ASK_AI_INPUT_ID}>
          {t("rideMap.investigation.askAi.title")}
        </label>
        <div className="ask-ai-input-row">
          <ChatTextFieldStyled
            id={ASK_AI_INPUT_ID}
            inputRef={textFieldRef}
            name="askAiMessage"
            placeholder={t("rideMap.investigation.askAi.placeholder")}
            value={userMessage}
            onChange={handleMessageChange}
            onKeyDown={onKeyDown}
            multiline
            rows={ASK_AI_INPUT_ROWS}
          />
          <IconButton
            size="small"
            color="primary"
            disabled={isSendDisabled}
            onClick={searchApply}
            aria-label={sendLabel}
            className="ask-ai-send"
            data-e2e={RIDE_INVESTIGATION_E2E.askAiSend}
          >
            {isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ArrowForward />
            )}
          </IconButton>
        </div>
      </div>
    </AskAiSectionStyled>
  );
};
